import { useTranslation } from 'react-i18next';
import { AlertCircle, Info } from 'lucide-react';
import type {
  TechnicianImportApplyResponse as Report,
  TechnicianImportValidateResponse as Preview,
} from 'api/technicianImportContract';
import { rowErrorReasonLabel } from '../importLabels';

/** The Excel row → name, for the report's failures (the apply answer carries rows only). */
const nameOfRow = (preview: Preview, row: number): string =>
  [...preview.additions, ...preview.changes, ...preview.exclusions.toExclude, ...preview.exclusions.toReinstate].find(
    (entry) => entry.row === row
  )?.name ?? '—';

/** CA13, CA18 — the counters, the rows that failed, and the no-message reminder. */
const ImportReport = ({ report, preview }: { report: Report; preview: Preview }) => {
  const { t } = useTranslation();
  const { counts } = report;
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-5 gap-2.5">
        {(
          [
            [t('Created'), counts.created],
            [t('Updated'), counts.updated],
            [t('Banned technicians'), counts.excluded],
            [t('Reinstated'), counts.reinstated],
            [t('Failures'), counts.failed],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5 rounded-xl border px-3.5 py-3">
            <dt className="text-[12.5px] text-muted-foreground">{label}</dt>
            <dd className="text-[22px] font-semibold tracking-tight">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="overflow-hidden rounded-xl border border-rose-400 dark:border-rose-500">
        <div className="flex items-center gap-1.5 border-b bg-muted/50 px-3.5 py-2 text-[12.5px] font-semibold text-rose-700 dark:text-rose-400">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          {t('Failures {{count}} · these rows were not saved', { count: counts.failed })}
        </div>
        <ul>
          {report.failed.map((failure) => (
            <li key={failure.row} className="grid grid-cols-[90px_180px_1fr] gap-3 border-b px-3.5 py-2.5 text-[13px] last:border-b-0">
              <span className="text-muted-foreground">{t('Row {{row}}', { row: failure.row })}</span>
              <span className="font-medium">{nameOfRow(preview, failure.row)}</span>
              <span>{rowErrorReasonLabel(t, failure.reason)}</span>
            </li>
          ))}
        </ul>
      </div>
      {counts.created > 0 && (
        <p className="flex items-start gap-2.5 rounded-lg bg-sky-50 px-3 py-2.5 text-[13px] text-sky-900 dark:bg-sky-950/50 dark:text-sky-300">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('The {{count}} technicians created received no message. Invite them to sign in with “Forgot password”.', { count: counts.created })}
        </p>
      )}
    </div>
  );
};

export default ImportReport;
